import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { api, fmt } from '../utils/api';
import { categoryIcon, categoryColor } from '../utils/categoryStyle';
import useCollapsible from '../utils/useCollapsible';

const FIXED_COLOR = '#4f7fff';
const ONEOFF_COLOR = '#f97316';

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { name, value } = payload[0];
  return (
    <div style={styles.tooltip}>
      <div style={styles.tooltipLabel}>{name}</div>
      <div style={styles.tooltipValue}>{fmt.currency(value)}</div>
    </div>
  );
};

export default function SpendingBreakdownCard({ refreshKey, onCategoryClick }) {
  const [collapsed, toggleCollapsed] = useCollapsible('spendingBreakdown');
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getSpendingBreakdown(period);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const categoryPieData = (data?.categoryBreakdown || [])
    .filter((c) => c.total > 0)
    .map((c) => ({ name: c.category, value: c.total, count: c.count }));

  const fixed = data?.fixedVsOneOff?.fixed;
  const oneOff = data?.fixedVsOneOff?.oneOff;
  const splitPieData = [
    { name: 'Fixed / recurring', value: fixed?.total || 0, color: FIXED_COLOR },
    { name: 'One-off / discretionary', value: oneOff?.total || 0, color: ONEOFF_COLOR },
  ].filter((d) => d.value > 0);

  const isEmpty = !loading && !error && categoryPieData.length === 0;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>🥧 Spending Breakdown</h2>
        </button>
        {!collapsed && (
        <div style={styles.pillGroup}>
          <button
            style={{ ...styles.pill, ...(period === 'week' ? styles.pillActive : {}) }}
            onClick={() => setPeriod('week')}
          >
            Week
          </button>
          <button
            style={{ ...styles.pill, ...(period === 'month' ? styles.pillActive : {}) }}
            onClick={() => setPeriod('month')}
          >
            Month
          </button>
        </div>
        )}
      </div>

      {!collapsed && (<>
      {loading && <p style={styles.empty}>Loading...</p>}
      {error && !loading && <p style={styles.errorText}>{error}</p>}
      {isEmpty && <p style={styles.empty}>No spending yet this {period}.</p>}

      {!loading && !error && categoryPieData.length > 0 && (
        <>
          <div style={styles.chartsRow}>
            <div style={styles.chartCol}>
              <div style={styles.chartLabel}>By category</div>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={categoryPieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={42}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {categoryPieData.map((entry, i) => (
                      <Cell key={entry.name} fill={categoryColor(i)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legend}>
                {categoryPieData.slice(0, 6).map((c, i) => (
                  <button
                    key={c.name}
                    type="button"
                    style={styles.legendRowBtn}
                    onClick={() => onCategoryClick?.(c.name, period)}
                    title={`View ${c.name} transactions`}
                  >
                    <span style={{ ...styles.legendDot, background: categoryColor(i) }} />
                    <span style={styles.legendLabel}>{categoryIcon(c.name)} {c.name}</span>
                    <span style={styles.legendValue}>{fmt.currency(c.value)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.chartCol}>
              <div style={styles.chartLabel}>Fixed vs one-off</div>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={splitPieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={42}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {splitPieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legend}>
                <div style={styles.legendRow}>
                  <span style={{ ...styles.legendDot, background: FIXED_COLOR }} />
                  <span style={styles.legendLabel}>Fixed / recurring</span>
                  <span style={styles.legendValue}>{fmt.currency(fixed?.total || 0)}</span>
                </div>
                <div style={styles.legendRow}>
                  <span style={{ ...styles.legendDot, background: ONEOFF_COLOR }} />
                  <span style={styles.legendLabel}>One-off / discretionary</span>
                  <span style={styles.legendValue}>{fmt.currency(oneOff?.total || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.cutSection}>
            <div style={styles.cutHeader}>
              🔒 Fixed costs this {period} <span style={styles.cutSubtext}>(utilities, insurance, recurring - hard to cut)</span>
            </div>
            {(fixed?.byCategory || []).length === 0 ? (
              <p style={styles.emptySmall}>None this {period}.</p>
            ) : (
              fixed.byCategory.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  style={styles.cutRowBtn}
                  onClick={() => onCategoryClick?.(c.category, period)}
                  title={`View ${c.category} transactions`}
                >
                  <span style={styles.cutRowLabel}>{categoryIcon(c.category)} {c.category}</span>
                  <span style={styles.cutValue}>{fmt.currency(c.total)}</span>
                </button>
              ))
            )}

            <div style={{ ...styles.cutHeader, marginTop: '1.1rem' }}>
              ✂️ One-off spending this {period} <span style={styles.cutSubtext}>(easiest to cut back - click one to see it, or recategorize it under Bills and Utilities/Healthcare to treat it as fixed)</span>
            </div>
            {(oneOff?.byCategory || []).length === 0 ? (
              <p style={styles.emptySmall}>None this {period}.</p>
            ) : (
              oneOff.byCategory.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  style={styles.cutRowBtn}
                  onClick={() => onCategoryClick?.(c.category, period)}
                  title={`View ${c.category} transactions`}
                >
                  <span style={styles.cutRowLabel}>{categoryIcon(c.category)} {c.category}</span>
                  <span style={{ ...styles.cutValue, color: ONEOFF_COLOR }}>{fmt.currency(c.total)}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
    width: '0.8rem',
  },
  pillGroup: { display: 'flex', gap: '0.4rem' },
  pill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  pillActive: {
    background: '#00d4aa',
    color: '#04120e',
    border: '1px solid #00d4aa',
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' },
  emptySmall: { color: '#4b5563', fontSize: '0.8rem', padding: '0.4rem 0' },
  errorText: { color: '#ef4444', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.25rem',
  },
  chartCol: { minWidth: 0 },
  chartLabel: {
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginBottom: '0.4rem',
    fontFamily: "'Syne', sans-serif",
  },
  legend: { marginTop: '0.6rem' },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem 0',
  },
  legendRowBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem 0.3rem',
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  legendDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  legendLabel: { color: '#d1d5db', fontSize: '0.78rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  legendValue: { color: '#fff', fontSize: '0.78rem', fontWeight: '600' },
  cutSection: {
    marginTop: '1.4rem',
    paddingTop: '1.1rem',
    borderTop: '1px solid #1e2a3a',
  },
  cutHeader: {
    color: '#fff',
    fontSize: '0.82rem',
    fontWeight: '700',
    marginBottom: '0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
  cutSubtext: { color: '#6b7280', fontWeight: '500', fontSize: '0.75rem' },
  cutRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.35rem 0',
    borderBottom: '1px solid #161f2c',
    fontSize: '0.82rem',
  },
  cutRowBtn: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0.35rem 0.3rem',
    borderBottom: '1px solid #161f2c',
    fontSize: '0.82rem',
    background: 'transparent',
    border: 'none',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderBottomColor: '#161f2c',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  cutRowLabel: { color: '#d1d5db' },
  cutValue: { color: '#fff', fontWeight: '600' },
  tooltip: {
    background: '#1a2332',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.5rem 0.7rem',
  },
  tooltipLabel: { color: '#9ca3af', fontSize: '0.72rem', marginBottom: '2px' },
  tooltipValue: { color: '#fff', fontSize: '0.85rem', fontWeight: '700' },
};
